import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import emailjs from '@emailjs/browser';
import Container from '../components/ui/Container';
import './ContactPage.css';

const ContactPage = () => {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();

    // Honeypot: real users never fill this hidden field. Bots that do get a
    // fake success so they don't retry, and no email is sent (protects the
    // EmailJS quota — the service/template IDs are public by design).
    if (form.current.elements.website?.value) {
      setSubmitMessage("Thank you for your inquiry. We'll review your requirements and respond with next steps.");
      setIsError(false);
      form.current.reset();
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    setIsError(false);

    // This uses the .env variables to securely send the form
    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        form.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        (result) => {
          setSubmitMessage("Thank you for your inquiry. We'll review your requirements and respond with next steps.");
          setIsError(false);
          form.current.reset(); // Resets the form fields on successful submission
        },
        (error) => {
          console.error('FAILED...', error.text); // Log error for debugging
          setSubmitMessage('Failed to send the message. Please try again later.');
          setIsError(true);
        }
      )
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="contact-page page-with-header">
      <Helmet>
        <title>Contact PySolutions — Tell us what you're building | Free quote</title>
        <meta name="description" content="Tell us what you're building. Free consultation for full-stack AI products, modern backends, cloud, and DevOps engagements. Senior-led, replies within 24 hours on weekdays." />
        <meta name="author" content="PySolutions" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://pysolutions.dev/contact" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pysolutions.dev/contact" />
        <meta property="og:title" content="Contact PySolutions — Tell us what you're building | Free quote" />
        <meta property="og:description" content="Tell us what you're building. Free consultation for full-stack AI products, modern backends, cloud, and DevOps engagements." />
        <meta property="og:image" content="https://pysolutions.dev/og-card.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="PySolutions contact page" />
        <meta property="og:site_name" content="PySolutions" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact PySolutions — Tell us what you're building | Free quote" />
        <meta name="twitter:description" content="Tell us what you're building. Free consultation for full-stack AI products, modern backends, cloud, and DevOps engagements." />
        <meta name="twitter:image" content="https://pysolutions.dev/og-card.jpg" />

        {/* LocalBusiness Schema for better local SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "PySolutions",
            "alternateName": "PySolutions Full-stack AI Engineering",
            "description": "Senior-led full-stack engineering for production AI products: backend, frontend, AI/ML, cloud, and DevOps shipped end to end.",
            "url": "https://pysolutions.dev",
            "logo": "https://pysolutions.dev/PySolutions.jpeg",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "areaServed": "Worldwide",
              "availableLanguage": ["en"],
              "url": "https://pysolutions.dev/contact"
            },
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "RS",
              "addressRegion": "Serbia"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 44.0165,
              "longitude": 21.0059
            },
            "areaServed": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": 44.0165,
                "longitude": 21.0059
              },
              "geoRadius": "Worldwide"
            },
            "serviceArea": {
              "@type": "AdministrativeArea",
              "name": "Worldwide"
            },
            "makesOffer": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Full-stack AI Product Engagement",
                  "description": "End-to-end build of a production AI product: backend, frontend, AI, cloud, and DevOps."
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "AI & Backend Consultation",
                  "description": "Free consultation for AI integration, backend architecture, and platform engagements."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="contact-hero section">
        <div className="contact-hero-background">
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
        </div>
        <Container>
          <div className="contact-hero-content">
            <div className="hero-badge">
              <span className="badge-icon">📞</span>
              Get in Touch
            </div>
            <h1 className="hero-title">
              Tell us what you're <span className="logo-highlight">building</span>
            </h1>
            <p className="hero-description">
              Senior-led, full-stack engineering with clear scopes and weekly demos. We reply within 24 hours on weekdays.
            </p>
            
            <div className="response-guarantees">
              <div className="guarantee-item">
                <div className="guarantee-icon">⚡</div>
                <span>Prompt response</span>
              </div>
              <div className="guarantee-item">
                <div className="guarantee-icon">💬</div>
                <span>Free consultation</span>
              </div>
              <div className="guarantee-item">
                <div className="guarantee-icon">✨</div>
                <span>No obligation</span>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section section">
        <div className="form-section-background">
          <div className="form-glow form-glow-1"></div>
          <div className="form-glow form-glow-2"></div>
        </div>
        <Container>
          <div className="form-container">
            <div className="form-header">
              <div className="form-badge">
                <span className="badge-icon">📝</span>
                  Start Your Project
              </div>
              <h2 className="form-title">Project <span className="logo-highlight">Inquiry</span></h2>
              <p className="form-subtitle">
                Share your requirements and we'll provide a comprehensive project assessment.
              </p>
            </div>

            <div className="form-wrapper">
              <div className="form-glow-bg"></div>
              <div className="form-beam"></div>
              <form ref={form} onSubmit={sendEmail} className="contact-form">
                {/* Honeypot field — hidden from real users, catches naive bots */}
                <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input type="text" id="website" name="website" tabIndex="-1" autoComplete="off" />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="fullName" className="form-label">
                      Full Name *
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="fullName"
                        name="from_name" // Corrected: Matches the template variable
                        className="form-input"
                        required
                        placeholder="Enter your full name"
                      />
                      <div className="input-focus-ring"></div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address *
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="email"
                        id="email"
                        name="from_email" // Corrected: Matches the template variable
                        className="form-input"
                        required
                        placeholder="your.email@company.com"
                      />
                      <div className="input-focus-ring"></div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="company" className="form-label">
                      Company/Project Title *
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="company"
                        name="company" // Correct: Matches the template variable
                        className="form-input"
                        required
                        placeholder="Your company or project name"
                      />
                      <div className="input-focus-ring"></div>
                    </div>
                  </div>

                  <div className="form-group form-group-full">
                    <label htmlFor="projectDescription" className="form-label">
                      Project Description *
                    </label>
                    <div className="input-wrapper">
                      <textarea
                        id="projectDescription"
                        name="message" // Correct: Matches the template variable
                        className="form-textarea"
                        required
                        rows="5"
                        placeholder="Brief description: API backend, AI chatbot, data processing system, hardware integration, etc. Include any specific requirements or timeline."
                      />
                      <div className="input-focus-ring"></div>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn-primary form-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Inquiry
                        <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                        </svg>
                      </>
                    )}
                  </button>
                  
                  {submitMessage && (
                    <div className={`submit-message ${isError ? 'error-message' : 'success-message'}`}>
                      <div className="message-icon">{isError ? '❌' : '✅'}</div>
                      {submitMessage}
                    </div>
                  )}
                </div>
              </form>

              <div className="form-footer">
                <p>We'll provide a detailed proposal including project scope, timeline, and investment.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Response Guarantees Section */}
      <section className="guarantees-section section">
        <div className="guarantees-background">
          <div className="guarantee-glow guarantee-glow-1"></div>
          <div className="guarantee-glow guarantee-glow-2"></div>
        </div>
        <Container>
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">🎯</span>
              Our Commitments
            </div>
            <h2 className="section-title">
              Professional <span className="logo-highlight">Service Standards</span>
            </h2>
            <p className="section-subtitle">
              Reliable commitments you can count on for every project
            </p>
          </div>

          <div className="guarantees-grid">
            <div className="guarantee-card guarantee-response" data-index="0">
              <div className="card-glow"></div>
              <div className="card-beam"></div>
              <div className="guarantee-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3>Timely Response</h3>
              <p>
                Professional response to all inquiries during business hours with personalized attention to your requirements
              </p>
            </div>

            <div className="guarantee-card guarantee-proposal" data-index="1">
              <div className="card-glow"></div>
              <div className="card-beam"></div>
              <div className="guarantee-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3>Comprehensive Proposal</h3>
              <p>
                Detailed project analysis with technical specifications, timeline, milestones, and transparent pricing
              </p>
            </div>

            <div className="guarantee-card guarantee-pressure" data-index="2">
              <div className="card-glow"></div>
              <div className="card-beam"></div>
              <div className="guarantee-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3>No Pressure Approach</h3>
              <p>
                Professional consultation with no obligations. Proceed only when our solution aligns with your vision
              </p>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default ContactPage;